import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBell,
  HiOutlinePencil,
  HiOutlineTrash,
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const Triggers = () => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTrigger, setEditingTrigger] = useState(null);
  const [formData, setFormData] = useState({
    triggerName: '',
    conditionType: '',
    conditionValue: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadTriggers();
    } else {
      setError('Please login to view triggers');
      setLoading(false);
    }
  }, [tenantId]);

  const loadTriggers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getTriggers(tenantId);
      setTriggers(data);
    } catch (err) {
      setError('Failed to load triggers. Please try again.');
      console.error('Error loading triggers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTriggers = triggers.filter((trigger) =>
    trigger.triggerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trigger.conditionType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingTrigger(null);
    setFormData({
      triggerName: '',
      conditionType: '',
      conditionValue: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (trigger) => {
    setEditingTrigger(trigger);
    setFormData({
      triggerName: trigger.triggerName || '',
      conditionType: trigger.conditionType || '',
      conditionValue: trigger.conditionValue || '',
      status: trigger.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteTrigger(id);
      await loadTriggers();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete trigger. Please try again.');
      console.error('Error deleting trigger:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.triggerName.trim()) {
      errors.triggerName = 'Trigger name is required';
    } else if (formData.triggerName.length > 100) {
      errors.triggerName = 'Trigger name must be less than 100 characters';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const triggerData = {
        tenantId: tenantId,
        triggerName: formData.triggerName.trim(),
        conditionType: formData.conditionType.trim() || null,
        conditionValue: formData.conditionValue.trim() || null,
        status: formData.status,
      };

      if (editingTrigger) {
        triggerData.triggerId = editingTrigger.triggerId;
        await masterDataService.updateTrigger(editingTrigger.triggerId, triggerData);
      } else {
        await masterDataService.createTrigger(triggerData);
      }

      await loadTriggers();
      setShowModal(false);
      setFormData({ triggerName: '', conditionType: '', conditionValue: '', status: 'Active' });
      setEditingTrigger(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save trigger. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTrigger(null);
    setFormData({ triggerName: '', conditionType: '', conditionValue: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading triggers..." />;
  }

  return (
    <div className="triggers-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Triggers</h2>
          <p>Manage automated triggers for notifications and alerts</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Trigger
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search triggers..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTriggers.length} {filteredTriggers.length === 1 ? 'trigger' : 'triggers'}
          </div>
        </div>

        {filteredTriggers.length === 0 ? (
          <div className="empty-state">
            <HiOutlineBell className="empty-icon" />
            <h3>No triggers found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first trigger'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                New Trigger
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Trigger Name</th>
                <th>Condition Type</th>
                <th>Condition Value</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTriggers.map((trigger) => (
                <tr key={trigger.triggerId}>
                  <td>{trigger.triggerId}</td>
                  <td className="name-cell">{trigger.triggerName}</td>
                  <td>{trigger.conditionType || '-'}</td>
                  <td>{trigger.conditionValue || '-'}</td>
                  <td>
                    <span className={`status-badge ${trigger.status?.toLowerCase()}`}>
                      {trigger.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {trigger.createdAt
                      ? new Date(trigger.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(trigger)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(trigger)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTrigger ? 'Edit Trigger' : 'New Trigger'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="trigger-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingTrigger ? 'Updating...' : 'Creating...') : (editingTrigger ? 'Update' : 'Create')}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="trigger-form" onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="triggerName">Trigger Name <span className="required">*</span></label>
                <input type="text" id="triggerName" value={formData.triggerName} onChange={(e) => setFormData({ ...formData, triggerName: e.target.value })} className={formErrors.triggerName ? 'error' : ''} placeholder="e.g., Low OEE Alert, High Downtime Alert" maxLength={100} />
                {formErrors.triggerName && <span className="field-error">{formErrors.triggerName}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="conditionType">Condition Type</label>
                <input type="text" id="conditionType" value={formData.conditionType} onChange={(e) => setFormData({ ...formData, conditionType: e.target.value })} placeholder="e.g., OEE, Downtime, Quality" maxLength={50} />
              </div>
              <div className="form-group">
                <label htmlFor="conditionValue">Condition Value</label>
                <input type="text" id="conditionValue" value={formData.conditionValue} onChange={(e) => setFormData({ ...formData, conditionValue: e.target.value })} placeholder="e.g., < 70, > 100, = 0" maxLength={50} />
              </div>
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select id="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button type="button" className="btn-danger" onClick={() => handleDelete(deleteConfirm.triggerId)}>Delete</button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>Are you sure you want to delete <strong>{deleteConfirm?.triggerName}</strong>?</p>
          <p className="warning-text">This action cannot be undone. The trigger will be marked as inactive.</p>
        </div>
      </SharedModal>
    </div>
  );
};

export default Triggers;

